<script setup>
import {computed, onMounted, onUnmounted, reactive, ref, shallowRef, watch} from "vue";
import {useGoTo} from 'vuetify';
import {useRoute, useRouter} from "vue-router";
import {ToastUtil} from "@/Utils/ToastUtil.js";
import {ApiWrapper} from "@/Utils/ApiWrapper.js";
import {EventStream} from "@/Utils/EventStream.js";
import {activeConversationId} from "@/Utils/ActiveConversation.js";
import {AxiosError} from "axios";
import {Permissions} from "@/Utils/Permissions";

/**
 * TODO
 * - Determine if Cookie saving of "last active conversation" is worth doing
 *   use onUnMounted to store last-convo id in cookie?
 */

const route = useRoute();
//const router = useRouter();   //TODO Might want to use in order to update URI when viewing specific convos
const goTo = useGoTo();

//----------------------------
// Claim filtering
//----------------------------

const claimsData = reactive({
    loading: false,
    search: shallowRef(''),
    perPage: 10,
    perPageOptions: [{value: 10, title: '10'}],
    headers: [
        {title: 'Claim Number', key: 'claimnumber', sortable: false},
        {title: 'Client', key: 'client', sortable: true},
        {title: 'Claim Type', key: 'claim_type', sortable: true},
        {title: 'CAT Code', key: 'claim_catcode', sortable: true},
        {title: 'Age', key: 'age', sortable: true},
    ],
    items: []
});

const getClaimsTableData = () => {
    claimsData.loading = true;
    ApiWrapper.get('conversations', 'get-available-claims')
        .then((response) => {
            claimsData.items = response.claims;
            claimsData.loading = false;
        })
        .catch((err) => {
            let apiErrMsg = (err instanceof AxiosError) ? err.response.data.message : err.message;
            ToastUtil.error(`[${err.code}] ${apiErrMsg}`);
            claimsData.items = [];
            claimsData.loading = false;
        });
};

const filteredClaimsData = computed(() => {
    if (claimsData.search == null || claimsData.search === '') {
        return claimsData.items;
    }

    return claimsData.items.filter(   //Other fields? client? Type?
        (item) => {
            return item.claimnumber?.toString().toLowerCase().includes(claimsData.search?.toString().toLowerCase());
        });
});

const GetIsFireType = (type) => {
    if (type) {
        return type.toString().toLowerCase().includes('fire');
    }
    return false;
}

//-----------------------------------------------------------------------------
// Adjusters List

//FIXME Below will NOT work. There are 2358 (at least) adjusters in Prod. MUST have some kind of streamed-chunking of data!
// - Additionally to above, sorting as No messages & Messages...do we need to display no new messages at all?
// -- Yes, zero messages are needed, as navigating away from view (to get some data, i.e. claim address) and back IS a likely use-case.
const adjustersData = reactive({
    loading: false,
    adjusters: [],
    selectedAdjuster: ref(0),
    tempSelectedAdjuster: 0,
    tempSearch: null,
    search: ref('')
});

const adjustersHeaders = [
    {title: 'Adjustors', key: 'name', sortable: false}
];

const getAdjustersData = () => {
    adjustersData.loading = true;

    ApiWrapper.get('conversations', 'get-adjusters-list')
        .then((response) => {
            adjustersData.adjusters = response.adjusters;
            adjustersData.loading = false;
            LoadConversationByUrl();  //cheesy
        })
        .catch((err) => {
            let apiErrMsg = (err instanceof AxiosError) ? err.response.data.message : err.message;
            ToastUtil.error(`[${err.code}] ${apiErrMsg}`);
            adjustersData.adjusters = [];
            adjustersData.loading = false;
        });
};

const SetConversationStateAdjuster = (name, id, isEmpty) => {
    adjustersData.selectedAdjuster = id;

    conversationData.adjuster = name;
    conversationData.adjusterId = id;

    if (isEmpty) {
        conversationData.empty_conversations = true;
        conversationData.messages = [];
        conversationData.loading = false;

        convoClaimsData.claims = [];
        convoClaimsData.loading = false;
    } else {
        conversationData.empty_conversations = false;
    }
};

//FIXME Below list needs to be paginated (server-side), and use v-intersect & v-infinite-scroll to "page" through data
// - The pagination also needs to be able to handle filtering
const AdjusterItemClickHandler = (event, row) => {
    if (adjustersData.selectedAdjuster === row.item.person_id)
        return; //same adjuster is clicked twice or more. halt

    conversationData.loading = true;

    if (row.item.conversation_id === null) {
        SetConversationStateAdjuster(row.item.name, row.item.person_id, true);
    } else {
        GetConversationMessages(
            row.item.conversation_id,
            row.item.name,
            row.item.person_id
        );
    }
};

//Whether the person whose conversation is open can be texted
const SelectedAdjusterTextsEnabled = () => {
    const adjuster = adjustersData.adjusters.find((a) => a.person_id === adjustersData.selectedAdjuster);
    return adjuster?.texts_enabled === true;
};

const IsSelectedAdjuster = (adjuster) => {
    return adjustersData.selectedAdjuster > 0 && adjustersData.selectedAdjuster === adjuster.person_id;
};

//Highlights the whole row of the conversation that's open
const AdjusterRowProps = ({item}) => {
    return {class: IsSelectedAdjuster(item) ? 'adjuster-row-active' : ''};
};

const GetSelectedAdjusterConvoId = async () => {
    for await (const [i, adj] of adjustersData.adjusters.entries()) {
        if (adj.person_id === adjustersData.selectedAdjuster) {
            return adj.conversation_id;
        }
    }
    return null;
}


//------------------------------------------------------------------
//  Messaging
//------------------------------------------------------------------

const formatTimestampByLocale = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleString()
};

let sendMessageText = ref('');

const conversationData = reactive({
    loading: false,
    empty_conversations: false,
    messages: [],
    search: '',
    adjuster: '',
    adjusterId: 0,
    itemsPerPage: 10
});

let filteredMessages = computed(() => {
    if (conversationData.search == null || conversationData.search === '') {
        return conversationData.messages;
    }

    return conversationData.messages.filter(
        (item) => {
            return item.body.toString().toLowerCase().includes(conversationData.search?.toString().toLowerCase());
        });
});

const GetConversationMessages = async (convoId, adjName, adjId) => {
    ApiWrapper.post('conversations', 'get-messages', {
        conversationid: convoId
    })
        .then((response) => {
            convoClaimsData.claims = [];
            convoClaimsData.loading = false;
            conversationData.messages = response.messages;
            conversationData.loading = false;

            if (!adjName || !adjId) {
                //Loading convo from URL id, use response data to complete message actions
                SetConversationStateAdjuster(response.person_name, response.person_id, false);
            } else {
                SetConversationStateAdjuster(adjName, adjId, false);
            }

            //Arrived from a notification (?message=<id>): go to that message instead of the oldest unread one
            if (!scrollToMessageFromUrl() && !hasUnreadMessages())
                scrollToOldestUnreadMessage();
        })
        .catch((err) => {
            let apiErrMsg = (err instanceof AxiosError) ? err.response.data.message : err.message;
            ToastUtil.error(`[${err.code}] ${apiErrMsg}`);

            SetConversationStateAdjuster('', 0, true);

            conversationData.messages = [];
            conversationData.loading = false;

            convoClaimsData.claims = [];
            convoClaimsData.loading = false;
        });
};

const LoadConversationByUrl = async () => {
    const convoId = route.params.conversationId;
    if (convoId) {
        await GetConversationMessages(convoId);
    }
};

const ClearSendMessage = () => {
    sendMessageText.value = '';
}

const hasUnreadMessages = () => {
    if (conversationData.messages.length === 0)
        return false;

    conversationData.messages.forEach((i) => {
        if (i.read === false)
            return true;
    });
}

const scrollToOldestUnreadMessage = () => {
//Note: this uses a timer because we need to give the DOM time to build and update; otherwise, the
//scroll events won't find a target to move to.
    setTimeout(() => {
        let msgElId = null;
        for (let m = conversationData.messages.length - 1; m > 0; m--) {
            //traverse msgs list backwards, find the _last_ occurrence of unread, and go
            if (conversationData.messages[m - 1].read === true && conversationData.messages[m].read === false) {
                msgElId = `#message-${conversationData.messages[m].message_id}`;
                break;
            }
        }

        if (msgElId === null) {
            msgElId = `#message-${conversationData.messages[conversationData.messages.length - 1].message_id}`;
        }

        if (msgElId !== null)
            goTo(msgElId, {container: '#conversationContainer', duration: 0, easing: 'linear'});
    }, 100); //delay for DOM build
};

//Scroll to, and briefly highlight, the message named in the URL (?message=<id>). Returns false if there isn't one to go to.
const scrollToMessageFromUrl = () => {
    const messageId = Number(route.query.message);
    if (!messageId || !conversationData.messages.some((m) => m.message_id === messageId))
        return false;

    setTimeout(() => { //delay for DOM build
        const msgElId = `message-${messageId}`;
        goTo('#' + msgElId, {container: '#conversationContainer', duration: 0, easing: 'linear'});
        StylizeNewPostedMessage(msgElId);
    }, 100);

    return true;
};

const StylizeNewPostedMessage = (id) => {
    const el = document.getElementById(id);
    if (el !== null) {
        el.classList.add("new-message");
        setTimeout(async () => {
            el.classList.remove("new-message");
        }, 2000);
    }
};

const ScrollToNewPostedMessage = () => {
    setTimeout(() => {
        let msgElId = `message-${conversationData.messages[conversationData.messages.length - 1].message_id}`;
        goTo('#' + msgElId, {container: '#conversationContainer', duration: 0, easing: 'linear'});
        StylizeNewPostedMessage(msgElId);
    }, 75); //delay for DOM build/update
};

//Simplicity to "generate" a new message object that mimics server-scoped format
//this eliminates need to load messages again, just because we added a new one.
//FIXME on new convo scenario, sender is person_id, not their name
const buildClientSideMessage = (serverResMsg) => {
    return {
        conversation_id: serverResMsg.conversation_id,
        message_id: serverResMsg.id,
        direction: serverResMsg.direction,
        created_at: formatTimestampByLocale(serverResMsg.created_at),
        sender: serverResMsg.sender,
        recipient: serverResMsg.recipient,
        read: true,
        body: serverResMsg.body,
        status: serverResMsg.status,
        error_code: serverResMsg.error_code
    }
};

const SendTextMessage = async () => {
//messaging is a simple stack and chronological, so we can never respond to anything except the last message
    if (sendMessageText.value === '' || sendMessageText.value === null) {
        ToastUtil.warningMsg(`Message contents required.\nFill out 'Send Message' textbox.`, 3500);
        return;

    } else if (conversationData.adjusterId === -1 || conversationData.adjuster === '') {
        //no adjuster selected
        ToastUtil.warningMsg(`Select Adjuster to send a text message to.`, 1250);
        return;
    }

    const newMessage = {
        message: sendMessageText.value,
        recipient: conversationData.adjusterId
    };

    ApiWrapper.post('conversations', 'send-message', newMessage)
        .then(async (res) => {

            let personIdx = null;
            for await (const [i, adj] of adjustersData.adjusters.entries()) {
                //FIXME this is awful, shouldn't need to loop all persons, will be huge in prod
                if (adj.person_id === adjustersData.selectedAdjuster) {
                    personIdx = i;
                    break;
                }
            }

            if (adjustersData.adjusters[personIdx].conversation_id === null) {
                adjustersData.adjusters[personIdx].conversation_id = res.message.conversation_id;
            }

            //The live update for this message can arrive before this response does
            const alreadyAdded = conversationData.messages.find((m) => m.message_id === res.message.id);
            if (alreadyAdded) {
                alreadyAdded.status = res.message.status;
                alreadyAdded.error_code = res.message.error_code;
            } else {
                conversationData.messages.push(buildClientSideMessage(res.message));
            }
            conversationData.empty_conversations = false;

            ScrollToNewPostedMessage();
            sendMessageText.value = '';

            ToastUtil.successMsg(res.toast, 1750);
        })
        .catch((err) => {
            let apiErrMsg = (err instanceof AxiosError) ? err.response.data.message : err.message;
            ToastUtil.error(`[${err.code}] ${apiErrMsg}`, 6000);
        });
};

//-----------------------------------------------------------------------------
//  Message status ticks (WhatsApp style) -------------------------------------

//Twilio error codes for texts that couldn't be delivered, in plain words
const MESSAGE_ERRORS = {
    '21610': 'Recipient has opted out of texts',
    '21211': 'Not a valid phone number',
    '21614': 'Not a valid mobile number',
    '30003': 'Phone is unreachable or turned off',
    '30005': 'Unknown destination phone',
    '30006': 'Landline or unreachable carrier',
    '30007': 'Message blocked by the carrier',
    'send_error': 'Message could not be sent'
};

//What to show next to an outgoing message. Texts we received have no delivery status.
const GetMessageStatusDisplay = (msg) => {
    if (msg.direction !== 'outbound')
        return null;

    switch (msg.status) {
        case 'new':
            return {icon: 'mdi-clock-outline', color: 'grey', label: 'Sending', failed: false};
        case 'accepted':
        case 'scheduled':
        case 'queued':
        case 'sending':
        case 'sent':
            return {icon: 'mdi-check', color: 'grey', label: 'Sent', failed: false};
        case 'delivered':
            return {icon: 'mdi-check-all', color: 'grey', label: 'Delivered', failed: false};
        case 'read':
            return {icon: 'mdi-check-all', color: 'blue', label: 'Read', failed: false};
        case 'undelivered':
        case 'failed':
        case 'canceled': {
            const reason = MESSAGE_ERRORS[msg.error_code] ?? (msg.error_code ? `Error ${msg.error_code}` : '');
            return {icon: 'mdi-alert-circle-outline', color: 'error', label: 'Not delivered', failed: true, title: reason ? `Not delivered: ${reason}` : 'Not delivered'};
        }
        default:
            return null;
    }
};

//-----------------------------------------------------------------------------
//  Live updates (server-sent events), with polling as a fallback -------------

const POLL_INTERVAL_MS = 15 * 1000;   //only polls while the event stream isn't connected

let pollIntervalId = null;
let offStreamHandlers = [];

const openConversationId = () => conversationData.messages[0]?.conversation_id ?? null;

//Let the rest of the app know which conversation is open (the notification bell skips ones the user can already see)
watch(openConversationId, (id) => {
    activeConversationId.value = id;
}, {immediate: true});

const isNearBottom = () => {
    const el = document.getElementById('conversationContainer');
    return !el || (el.scrollHeight - el.scrollTop - el.clientHeight) < 150;
};

const scrollToLatestMessage = () => {
    setTimeout(() => { //delay for DOM build
        const last = conversationData.messages[conversationData.messages.length - 1];
        if (last)
            goTo(`#message-${last.message_id}`, {container: '#conversationContainer', duration: 0, easing: 'linear'});
    }, 75);
};

//Pull the open conversation again and merge it in: add messages we don't have, update statuses.
//Unlike GetConversationMessages it leaves the rest of the screen alone, so it's safe to run in the background.
const refreshOpenConversation = async () => {
    const convoId = openConversationId();
    if (!convoId || conversationData.loading)
        return;

    try {
        const response = await ApiWrapper.post('conversations', 'get-messages', {conversationid: convoId});
        if (openConversationId() !== convoId)
            return; //switched to another conversation while waiting

        const stickToBottom = isNearBottom();
        let added = 0;

        for (const incoming of response.messages) {
            const existing = conversationData.messages.find((m) => m.message_id === incoming.message_id);
            if (existing) {
                existing.status = incoming.status;
                existing.error_code = incoming.error_code;
            } else {
                conversationData.messages.push(incoming);
                added++;
            }
        }

        //A text from them can turn their texting back on, so the send box follows the server
        const adjuster = adjustersData.adjusters.find((a) => a.conversation_id === convoId);
        if (adjuster)
            adjuster.texts_enabled = response.texts_enabled === true;

        //Only follow new messages if they were already looking at the bottom
        if (added > 0 && stickToBottom)
            scrollToLatestMessage();
    } catch (err) {
        console.error('Could not refresh conversation', err);
    }
};

//Update the adjusters list without the side effects of getAdjustersData (which also loads the URL's conversation)
const refreshAdjustersList = async () => {
    try {
        const response = await ApiWrapper.get('conversations', 'get-adjusters-list');
        adjustersData.adjusters = response.adjusters;
    } catch (err) {
        console.error('Could not refresh adjusters list', err);
    }
};

//A conversation message was created or changed: {conversationId, messageId, direction, status, errorCode}
const onConversationEvent = (event) => {
    //A message already on screen: its status changed
    const message = conversationData.messages.find((m) => m.message_id === event.messageId);
    if (message) {
        message.status = event.status;
        message.error_code = event.errorCode;
        return;
    }

    //A new message in the open conversation
    if (event.conversationId === openConversationId()) {
        refreshOpenConversation();
        return;
    }

    //A new text in a conversation that isn't open: show it as unread in the list
    if (event.direction === 'inbound') {
        const adjuster = adjustersData.adjusters.find((a) => a.conversation_id === event.conversationId);
        if (adjuster) {
            adjuster.new_messages = (Number(adjuster.new_messages) || 0) + 1;
        } else {
            refreshAdjustersList(); //a conversation this list doesn't know about yet
        }
    }
};

//Reaching the newest message marks the whole conversation read; older unseen messages don't matter once you're at the end
const markingReadUpTo = new Map();     //conversation id -> newest message id already sent, so scrolling doesn't resend it
const MarkConversationRead = async (msg) => {
    const hasUnread = conversationData.messages.some((m) => m.read === false && m.message_id <= msg.message_id);
    //Nothing unread up to here, or this message (or a newer one) was already sent for this conversation: no call needed
    if (!hasUnread || (markingReadUpTo.get(msg.conversation_id) ?? 0) >= msg.message_id)
        return;

    markingReadUpTo.set(msg.conversation_id, msg.message_id);
    try {
        await ApiWrapper.post('conversations', 'mark-messages-read', {message_ids: [msg.message_id]});

        conversationData.messages.forEach((m) => {
            if (m.message_id <= msg.message_id) m.read = true;    //everything up to the newest is read now
        });
        const adjuster = adjustersData.adjusters.find((a) => a.conversation_id === msg.conversation_id);
        if (adjuster) adjuster.new_messages = 0;    //clear the count in the adjusters list, if they're in it
    } catch (err) {
        markingReadUpTo.delete(msg.conversation_id);    //let the next scroll try again
        console.error('Could not mark conversation read', err);
    }
};

const OnMessageDisplayed = (isIntersecting, entries) => {
    //Only act when the message scrolls into view, not when it leaves
    if (!isIntersecting)
        return;

    const target = entries[0].target;
    const idStr = target.id.toString();
    const msgId = parseInt(idStr.substring(idStr.indexOf('-') + 1));
    const msg = conversationData.messages.find((m) => m.message_id === msgId);
    //Not a message we have loaded (e.g. the conversation changed while scrolling)
    if (!msg)
        return;

    //Briefly highlight messages that were unread
    if (msg.read === false) {
        target.classList.add("new-message");
        setTimeout(() => target.classList.remove("new-message"), 2000);
    }

    //Seeing the newest message means they've reached the end: mark the whole conversation read
    if (msg === conversationData.messages[conversationData.messages.length - 1]) {
        MarkConversationRead(msg);
    }
};


//-----------------------------------------------------------------------------
//  Conversation Claim(s) Data ------------------------------------------------

const convoClaimsData = reactive({
    loading: false,
    claims: [],
    list_size: 5
});

const contextEnableUnassignableShow = ref(false);

watch(conversationData, async (val) => {
    if (conversationData.messages.length === 0 || conversationData.loading === true)
        return; //skip sequence starting

    if (convoClaimsData.claims.length === 0 && adjustersData.selectedAdjuster > 0) {
        //only update the claims list when the adjuster convo data actually changes. Ignore filtered messages
        let convoId = await GetSelectedAdjusterConvoId();
        if (convoId) {
            getConvoClaimData(convoId);
        } else {
            console.error('Could not find active conversation id', {conversationId: convoId});
            await ToastUtil.error('Could not find active conversation id');
        }
    }
});

const ForceUpdateConvoClaimData = async () => {
    convoClaimsData.claims = [];
    getConvoClaimData(await GetSelectedAdjusterConvoId());
};

watch(contextEnableUnassignableShow, async (val) => {
    if (contextEnableUnassignableShow.value === true && convoClaimsData.claims.length < 1) {
        let convoIds = await parseConvoMessages();
        if (convoIds.length > 0)
            getConvoClaimData(convoIds);
    } else {
        convoClaimsData.claims = [];
        convoClaimsData.loading = false;
    }
});


//Should only be called after conversation data loaded and parsed
const getConvoClaimData = (conversationId) => {
    if (!conversationId) {
        console.error(`getConvoClaimData - Error invalid conversation id`, {conversationId});
        ToastUtil.error(`Error invalid conversation id`);
    }

    convoClaimsData.loading = true;

    ApiWrapper.post('conversations', 'get-context-data', {
        conversation_id: conversationId
    })
        .then((response) => {
            convoClaimsData.loading = false;
            convoClaimsData.claims = [];
            for (let c = 0; c < response.claims.length; c++) {
                if (response.claims[c].claim_assignable === true) {
                    convoClaimsData.claims.push(response.claims[c]);
                }
            }
        })
        .catch((err) => {
            let apiErrMsg = (err instanceof AxiosError) ? err.response.data.message : err.message;
            ToastUtil.error(`[${err.code}] ${apiErrMsg}`);
            convoClaimsData.claims = [];
            convoClaimsData.loading = false;
        });
};

const RevokeAdjusterAssignment = (claimId) => {
    if (!claimId || !Number.isInteger(claimId)) {
        ToastUtil.error(`Invalid ClaimID[${claimId}], revoke failure.`);
    }

    const adjusterId = conversationData.adjusterId;

    ApiWrapper.post('conversations', 'revoke-adjuster', {
        adjusterid: adjusterId,
        claimid: claimId
    })
        .then(async (res) => {
            await ForceUpdateConvoClaimData();
            getClaimsTableData();
            conversationData.messages.push(buildClientSideMessage(res.message, false, null));
            ScrollToNewPostedMessage();
            ToastUtil.infoMsg(`${res.toast}`, 5000);
        })
        .catch((err) => {
            let apiErrMsg = (err instanceof AxiosError) ? err.response.data.message : err.message;
            ToastUtil.error(`[${err.code}] ${apiErrMsg}`, 6000);
        });
};

const AssignAdjusterToClaim = (claimId) => {
    if (!claimId || !Number.isInteger(claimId)) {
        ToastUtil.error(`Invalid ClaimID[${claimId}], assign failure.`);
    }

    const adjusterId = adjustersData.selectedAdjuster;

    ApiWrapper.post('conversations', 'assign-adjuster', {
        adjusterid: adjusterId,
        claimid: claimId
    })
        .then(async (res) => {
            await ForceUpdateConvoClaimData();
            getClaimsTableData();
            conversationData.messages.push(buildClientSideMessage(res.message, false, null));
            ScrollToNewPostedMessage();
            ToastUtil.infoMsg(`${res.toast}`, 5000);
        })
        .catch((err) => {
            let apiErrMsg = (err instanceof AxiosError) ? err.response.data.message : err.message;
            ToastUtil.error(`[${err.code}] ${apiErrMsg}`, 6000);
        });
};


//    Combination Filtering     -----------------------------------------------
// These filter trigger events in other data driven elements

// Clicking a claim narrows the adjusters list to the people that claim is offered to.
// Everything below is computed from the current lists, so refreshes, assignments and revokes re-filter on their own.
const selectedClaimId = ref(null);

//The clicked claim, while it's still assignable. Once it's assigned elsewhere it drops out and the filter turns off.
const selectedClaim = computed(() => claimsData.items.find((claim) => claim.id === selectedClaimId.value) ?? null);

const shownAdjusters = computed(() => {
    if (!selectedClaim.value) {
        return adjustersData.adjusters;
    }
    const adjusterIds = selectedClaim.value.cxt_adjusters ?? [];
    return adjustersData.adjusters.filter((adj) => adjusterIds.includes(adj.person_id));
});

const ClearClaimFilter = () => {
    selectedClaimId.value = null;

    adjustersData.search = '';
    adjustersData.selectedAdjuster = 0;

    conversationData.adjuster = '';
    conversationData.adjusterId = 0;
    conversationData.adjusterConvoIds = null;
    conversationData.empty_conversations = false;

    conversationData.messages = [];
    convoClaimsData.claims = [];
};

//Clearing the claim search, or searching for something else, turns the filter off
watch(() => claimsData.search, (search) => {
    if (selectedClaimId.value === null) return;

    const stillShown = search && selectedClaim.value?.claimnumber?.toString().toLowerCase().includes(search.toString().toLowerCase());
    if (!stillShown) {
        ClearClaimFilter();
    }
});

const ClaimViewFilter = (claim) => {
    selectedClaimId.value = claim.id;
    claimsData.search = claim.claimnumber;
    adjustersData.search = '';
    adjustersData.selectedAdjuster = 0;
};


//-----------------------------------------------------------------------------
//   Component Wide Hooks   ---------------------------------------------------

//Clicking a notification while this screen is already open only changes the URL
watch(() => [route.params.conversationId, route.query.message], async () => {
    const convoId = Number(route.params.conversationId);
    if (!convoId || adjustersData.loading)
        return;

    const openConvoId = conversationData.messages[0]?.conversation_id;
    const messageId = Number(route.query.message);

    if (openConvoId === convoId && conversationData.messages.some((m) => m.message_id === messageId)) {
        scrollToMessageFromUrl();   //same conversation, message already loaded
    } else {
        conversationData.loading = true;
        await GetConversationMessages(convoId);
    }
});

onMounted(async () => {
    getAdjustersData();
    getClaimsTableData();

    offStreamHandlers = [
        EventStream.on('conversation', onConversationEvent),
        EventStream.on('resync', () => refreshOpenConversation()),
        EventStream.on('open', ({reconnected}) => {
            if (reconnected) refreshOpenConversation();
        }),
    ];

    //Only while this screen is open, and only if the stream isn't doing the work
    pollIntervalId = setInterval(() => {
        if (!EventStream.connected.value && !document.hidden)
            refreshOpenConversation();
    }, POLL_INTERVAL_MS);
});

onUnmounted(() => {
    activeConversationId.value = null;
    clearInterval(pollIntervalId);
    offStreamHandlers.forEach((off) => off());
});


//-----------------------------------------------------------------------------
//  Content Stylization / CSS  ------------------------------------------------

const GetConversationMessageClass = (msg, idx) => {
    let css = ' ';
    switch (msg.direction) {
        case 'system':
            css += 'text-center systemMessage ma-0 mt-2 mb-2 pa-0'
            break;
        case 'inbound':
            css += 'float-right inboundMessage w-50 ma-0 mt-2 mb-2'
            break;
        case 'outbound':
            css += 'float-left outboundMessage w-50 ma-0 mt-2 mb-2'
            break;
    }
    return css;
};

//How a system note looks, from its status. Assign saves 'claim_assigned', Reject saves 'claim_revoked';
//older notes ('completed') get the plain look. Revoked is gray, not red: red means a message failed.
const SYSTEM_EVENT_DISPLAY = {
    claim_assigned: {icon: 'mdi-check-circle-outline', color: 'success', css: 'systemEvent-assigned'},
    claim_revoked: {icon: 'mdi-close-circle-outline', color: 'grey-darken-1', css: 'systemEvent-revoked'},
    texting_opt_in: {icon: 'mdi-cellphone-message', color: 'blue', css: 'systemEvent-optIn'},
    texting_opt_out: {icon: 'mdi-cellphone-off', color: 'grey-darken-1', css: 'systemEvent-revoked'}
};
const SYSTEM_EVENT_DEFAULT = {icon: 'mdi-information-outline', color: 'grey-darken-1', css: 'systemEvent-default'};

const GetSystemEventDisplay = (msg) => SYSTEM_EVENT_DISPLAY[msg.status] ?? SYSTEM_EVENT_DEFAULT;

const GetMessageColumnClass = (msg) => {
    if (msg.direction === 'system') {
        return 'text-center align-center';
    } else if (msg.direction === 'outbound') {
        return 'text-right align-left';
    } else if (msg.direction === 'inbound') {
        return 'text-left';
    }
};


</script>


<style scoped>
/* The row in the adjusters list for the open conversation. :deep because the rows belong to v-data-table */
:deep(tr.adjuster-row-active > td) {
    background: rgba(var(--v-theme-primary), 0.14);
}

:deep(tr.adjuster-row-active > td:first-child) {
    box-shadow: inset 4px 0 0 rgb(var(--v-theme-primary));
}

.new-message { /* added programmatically */
    background-color: rgba(74, 144, 226, 0.185);
    transition: background-color;
    transition-duration: 1.25s;
}

/* System notes: a thin line across the thread with a chip in the middle */
.systemEvent {
    display: flex;
    align-items: center;
    gap: 12px;
}

.systemEvent::before,
.systemEvent::after {
    content: '';
    flex: 1;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.systemEvent-chip {
    max-width: 80%;
    border-radius: 12px;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    background: rgb(var(--v-theme-surface));
    text-align: center;
}

.systemEvent-body {
    font-size: 0.875rem;
}

.systemEvent-meta {
    font-size: 0.75rem;
    opacity: 0.6;
}

.systemEvent-assigned .systemEvent-chip {
    background: rgba(var(--v-theme-success), 0.08);
    border-color: rgba(var(--v-theme-success), 0.35);
}

.systemEvent-optIn .systemEvent-chip {
    background: rgba(var(--v-theme-info), 0.08);
    border-color: rgba(var(--v-theme-info), 0.35);
}

.systemEvent-revoked .systemEvent-chip {
    background: rgba(var(--v-theme-on-surface), 0.04);
}

.systemEvent-revoked .systemEvent-body {
    opacity: 0.75;
}

.systemMessage {
    /*background-color: rgba(0, 100, 255, 0.725);*/
    background-color: rgba(var(--v-theme-primary), 0.7);
    color: white;
    width: 100%;
    min-width: 100%;
}

.messageTimestamp {
    color: rgba(0, 0, 0, 0.5);
}

.inboundMessage { /* added programmatically */
    /*background-color: rgba(255, 155, 0, 0.1);*/
    background-color: rgba(var(--v-theme-warning), 0.15);
    max-width: 88%;
    min-width: 60%;
    text-align: left;
}

.outboundMessage { /* added programmatically */
    /*background-color: rgba(0, 150, 255, 0.1);*/
    background-color: rgba(var(--v-theme-info), 0.15);
    max-width: 88%;
    min-width: 60%;
    text-align: left;
}

.adjustersList {
    min-height: 70vh;
    max-height: 70vh;
}

.adjustersHeader {
    font-weight: normal;
}

.conversations {
    min-height: 63vh;
    max-height: 63vh;
    width: 100%;
    /* background-image: linear-gradient(to bottom right, rgba(255, 255, 255, 1), rgba(192, 192, 200, 0.1)) !important; */
}

.messagingCard {
    min-height: 16vh;
}

.claimLink {
    text-decoration: underline;
    color: #0d47a1;
}

.convoClaimsInfoPane {
    max-width: 450px;
    max-height: 650px;
    min-width: 380px;
    /*min-height: 400px;*/
    position: absolute;
    top: 0;
    right: 0;
}

.fire-claim {
    background-color: rgba(var(--v-theme-warning), 0.75);
    color: #000;
}

.normal-claim {
    background-color: rgba(var(--v-theme-primary), 0.7);
    color: #fff;
}

</style>

<template>

    <v-row no-gutters class="mb-3">
        <v-col cols="12" class="pa-0 pr-2">
            <v-card elevation="4" class="" variant="elevated">
                <v-card-title class="pa-0 ma-0">
                    <!-- <v-btn @click="DumpData" class="align-center w-25 ma-3 d-inline-block float-left" color="warning">DUMP DATA</v-btn> -->
                    <v-toolbar class="pa-0 ma-0" density="compact" color="white" flat>
                        <v-toolbar-title class="pa-0 ma-0 pl-5" style="font-size: large;">Assignable Claims</v-toolbar-title>
                        <!-- TODO rejig to make this actually search _all_ claims -->
                        <v-text-field
                            v-model="claimsData.search"
                            label="Search Claim Numbers"
                            prepend-inner-icon="mdi-magnify"
                            variant="solo-filled"
                            hide-details
                            single-line
                            v-if="!claimsData.loading"
                            clearable
                            style="max-width: 500px;"
                        ></v-text-field>
                    </v-toolbar>
                </v-card-title>
                <v-progress-linear :active="true" :indeterminate="claimsData.loading" height="2" class="ma-0" color="primary" bg-color="text-darken-4"></v-progress-linear>
                <v-card-text class="pa-0 pt-5 pl-5 pb-3 ma-0">
                    <v-data-table
                        :items="filteredClaimsData"
                        :headers="claimsData.headers"
                        :loading="claimsData.loading"
                        :items-per-page="claimsData.perPage"
                        :items-per-page-options="claimsData.perPageOptions"
                        hover
                        density="compact"
                        loading-text="Loading... Please wait"
                    >
                        <template v-slot:[`item.claimnumber`]="{ item }">
                            <a @click="ClaimViewFilter(item)" class="claimLink">{{ item.claimnumber }}</a>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>

    <v-row>
        <!-- === Adjusters Listing === -->
        <v-col cols="3" class="pr-3">
            <v-card elevation="4" class="pa-4">
                <h3 class="adjustersHeader mb-3">Adjusters</h3>
                <v-progress-linear :active="true" :indeterminate="adjustersData.loading" height="2" class="ma-0" color="primary" bg-color="text-darken-4"></v-progress-linear>
                <v-text-field
                    v-model="adjustersData.search"
                    density="compact"
                    label="Search Names"
                    prepend-inner-icon="mdi-magnify"
                    variant="solo-filled"
                    flat
                    hide-details
                    single-line
                    clearable
                    v-if="!adjustersData.loading"
                    class="mt-3"
                ></v-text-field>
                <v-container flex class="overflow-y-auto mt-4 pr-2 adjustersList">
                    <v-data-table-virtual
                        hide-default-footer
                        hide-default-header
                        density="compact"
                        hover
                        select-strategy="single"
                        disable-pagination
                        :items-per-page="-1"
                        :headers="adjustersHeaders"
                        :items="shownAdjusters"
                        :filter-keys="['name']"
                        v-model:search="adjustersData.search"
                        :loading="adjustersData.loading"
                        loading-text="Loading... Please wait"
                        fixed-header
                        :row-props="AdjusterRowProps"
                        @click:row="AdjusterItemClickHandler"
                    >
                        <template v-slot:[`item.name`]="{ item }">
                            <div class="d-flex align-center justify-space-between">
                                <a v-if="item.name" :class="['text-lg-body-1', 'text-blue-grey-darken-4', { 'font-weight-bold': IsSelectedAdjuster(item) }]"> {{ item.name }} </a>
                                <div class="d-flex align-center">
                                    <!-- inline: sits in the row at the right edge instead of floating over the name -->
                                    <v-badge inline class="ml-2 pa-0" color="info" :content="item.new_messages" v-if="item.new_messages > 0"></v-badge>
                                    <v-icon v-if="item.texts_enabled !== true" class="ml-2" color="error" size="small" icon="mdi-message-off-outline" title="Texting is not enabled"></v-icon>
                                </div>
                            </div>
                        </template>
                    </v-data-table-virtual>
                </v-container>
            </v-card>
        </v-col>

        <v-col cols="9" class="pl-3">

            <!-- === Active Conversation === -->
            <v-row no-gutters class="pt-0 pr-2">
                <v-col cols="8">
                    <v-card elevation="4" class="pa-0 position-relative">
                        <v-toolbar density="compact" color="white" flat>
                            <v-toolbar-title class="text-display-large">
                                Messages <span class="ma-0" v-if="!conversationData.loading">{{ conversationData.adjuster === '' ? '' : ' - ' + conversationData.adjuster }}</span>
                            </v-toolbar-title>
                            <v-text-field
                                v-model="conversationData.search"
                                prepend-inner-icon="mdi-magnify"
                                label="Search Messages"
                                variant="solo-filled"
                                single-line
                                hide-details
                                clearable
                                style="max-width: 500px;"
                            ></v-text-field>
                        </v-toolbar>

                        <v-progress-linear :active="true" :indeterminate="conversationData.loading" height="2" class="ma-0" color="primary" bg-color="text-darken-4"></v-progress-linear>

                        <v-row class="overflow-y-auto conversations" align-content-lg="start" id="conversationContainer">
                            <v-col cols="12" class="pl-8 pr-8">
                                <!-- TODO: Review if elements get too high, we'll need to change this to a virtual-table or similar -->
                                <v-data-iterator
                                    ref="messageIterable"
                                    v-if="!conversationData.noconversations"
                                    :items="filteredMessages"
                                    :itemsPerPage="conversationData.messages.length"
                                    :loading="conversationData.loading"
                                    loading-text="Loading... Please wait"
                                    class="ma-0 mt-5"
                                >
                                    <template v-slot:default="{ items }">
                                        <template v-for="(item, i) in items" :key="i">
                                            <v-row v-if="item.raw.direction !== 'system' ">
                                                <v-col cols="12" :class="'pa-0 ma-1 ' + GetMessageColumnClass(item.raw)">
                                                    <v-card
                                                        variant="text"
                                                        density="compact"
                                                        :class="'pa-3 ma-1 ' + GetConversationMessageClass( item.raw )"
                                                        :id="`message-${item.raw.message_id}`"
                                                        v-intersect="{ handler: OnMessageDisplayed, options: { threshold: [0, 0.65, 1.0] } }"
                                                    >
                                                        <v-card-subtitle class="pt-0 pb-1 ma-0 text-left messageTimestamp">
                                                            {{ item.raw.sender + ' at ' + formatTimestampByLocale(item.raw.created_at) }}
                                                            <span
                                                                v-if="GetMessageStatusDisplay(item.raw)"
                                                                :class="GetMessageStatusDisplay(item.raw).failed ? 'text-error font-weight-medium' : ''"
                                                                :title="GetMessageStatusDisplay(item.raw).title || GetMessageStatusDisplay(item.raw).label"
                                                            >
                                                                <v-icon size="small" :color="GetMessageStatusDisplay(item.raw).color" class="ml-1">
                                                                  {{ GetMessageStatusDisplay(item.raw).icon }}
                                                                </v-icon>
                                                                <template v-if="GetMessageStatusDisplay(item.raw).failed">
                                                                  {{ GetMessageStatusDisplay(item.raw).label }}
                                                                </template>
                                                            </span>
                                                        </v-card-subtitle>
                                                        <v-card-text class="pa-0 pl-4 text-left">
                                                            <pre class="text-pre-wrap">{{ item.raw.body }}</pre>
                                                            <!-- If we ever need emoji support (yuck), above will need a callback handler/formatter -->
                                                        </v-card-text>
                                                    </v-card>
                                                </v-col>
                                            </v-row>
                                            <v-row v-if="item.raw.direction === 'system' ">
                                                <v-col cols="12" class="text-center">
                                                    <!-- An event in the thread (claim assigned / revoked): a quiet line with a chip, not a message bubble -->
                                                    <div
                                                        class="systemEvent my-2"
                                                        :class="GetSystemEventDisplay(item.raw).css"
                                                        :id="`message-${item.raw.message_id}`"
                                                        v-intersect="{ handler: OnMessageDisplayed, options: { threshold: [0, 0.65, 1.0] } }"
                                                    >
                                                        <div class="systemEvent-chip px-3 py-1">
                                                            <div class="systemEvent-body">
                                                                <v-icon size="small" class="mr-1" :color="GetSystemEventDisplay(item.raw).color" :icon="GetSystemEventDisplay(item.raw).icon"></v-icon>
                                                                {{ item.raw.body }}
                                                            </div>
                                                            <div class="systemEvent-meta">
                                                                {{ item.raw.sender ? `${item.raw.sender} · ` : '' }}{{ formatTimestampByLocale(item.raw.created_at) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </template>
                                    </template>
                                </v-data-iterator>

                            </v-col>
                        </v-row>

                        <!-- Texting isn't enabled for this person: no message box, just say why -->
                        <v-alert
                            v-if="Permissions.checkPermissions(['conversations.send-messages']) && adjustersData.selectedAdjuster > 0 && !SelectedAdjusterTextsEnabled()"
                            type="warning"
                            variant="tonal"
                            class="mt-3 mx-2"
                            icon="mdi-message-off-outline"
                        >
                            {{ conversationData.adjuster || 'This person' }} can't be messaged. Texting is not enabled.
                        </v-alert>
                        <v-card elevated="0" variant="flat" class="mt-3 messagingCard" v-else-if="Permissions.checkPermissions(['conversations.send-messages'])">
                            <v-row class="pa-2 pb-0 ma-0 ga-0">
                                <v-col cols="12" class="ga-0 pa-0 ma-0">
                                    <v-textarea label="Send Message" variant="outlined" class="ma-0 mb-0 mr-2 ml-2 pa-0" rows="2" v-model="sendMessageText"></v-textarea>
                                </v-col>
                            </v-row>
                            <v-row class="ga-0 ma-0 pa-0 pb-2">
                                <v-col cols="7" class="pa-0 pl-3 pt-0 gt-0 text-left">
                                </v-col>
                                <v-col cols="5" class="pa-0 pr-3 pt-0 gt-0 text-right">
                                    <v-btn class="ml-1 mt-0 text-left" @click="ClearSendMessage" variant="elevated">Clear</v-btn>
                                    <v-spacer class="d-inline mr-3 ml-2"></v-spacer>
                                    <v-btn class="ml-1 mt-0" color="primary" variant="elevated" @click="SendTextMessage">Send Message</v-btn>
                                </v-col>
                            </v-row>
                        </v-card>
                    </v-card>
                </v-col>
                <v-col cols="4" class="pl-6 align-center position-relative">
                    <!-- Conversation(s) Context Info Card | Can list multiple claims | Assignable Claims Only -->
                    <v-card
                        elevation="4"
                        class="position-sticky top-0 right-0 ma-0 mr-0 pa-0 overflow-hidden overflow-y-auto convoClaimsInfoPane"
                        v-if="!conversationData.loading && convoClaimsData.claims.length > 0 && conversationData.empty_conversations === false"
                    >
                        <v-card-title class="text-lg-body-1 ma-0 pa-2 bg-grey-lighten-2"> Assignable Claims</v-card-title>

                        <v-progress-linear :active="true" :indeterminate="convoClaimsData.loading" height="2" class="ma-0 pa-0" color="primary" bg-color="text-darken-4"></v-progress-linear>

                        <v-card-text class="ma-0 pa-2 pb-3 align-content-center">

                            <v-data-iterator
                                ref="convoClaimsDataList"
                                :items="convoClaimsData.claims"
                                :itemsPerPage="-1"
                                :loading="convoClaimsData.loading"
                                loading-text="Loading Claims data... Please wait"
                                class="ma-0 mt-3"
                            >
                                <template v-slot:default="{ items }">
                                    <template v-for="(item, i) in items" :key="i">
                                        <v-card density="compact" class="ma-0 mb-8 pa-0 bg-grey-lighten-4 ml-5 mr-5" elevation="4">
                                            <v-card-title :class="'pa-1 pl-2 pt-2 text-sm-body-1 ' + (GetIsFireType(item.raw.claim_type) ? 'fire-claim' : 'normal-claim')">
                                                <span class="ma-0 mr-2 pa-0" v-if="GetIsFireType(item.raw.claim_type)"> <v-icon color="white" size="28">mdi-fire-circle</v-icon> </span>
                                                Claim Number: {{ item.raw.claimnumber }}
                                            </v-card-title>
                                            <v-card-subtitle class="pb-1 text--black"> Client: {{ item.raw.client }}</v-card-subtitle>
                                            <v-card-text class="pa-0 pl-5 pr-4 pb-1">
                                                <v-row no-gutters>
                                                    <v-col cols="12" class="align-left">
                                                        <!-- TODO cleanup and format below better, clumped messed atm -->
                                                        <span class="ma-0 pa-0 pt-1 pb-1 w-100">
                                                          Type: {{ item.raw.claim_type.toString().trim().substring(0, item.raw.claim_type.toString().lastIndexOf(',')) }} <br/>
                                                          CAT Code: {{ item.raw.catcode ? item.raw.catcode : '' }}<br/>
                                                          State: {{ item.raw.claim_state }}<br/>
                                                          Age: {{ item.raw.agedays > 1 ? item.raw.agedays + ' Days' : item.raw.agedays + ' Day' }}
                                                        </span>
                                                    </v-col>
                                                </v-row>
                                                <v-row no-gutters class="pa-0 pt-4 pb-2" v-if="item.raw.claim_assignable === true && Permissions.checkPermissions(['claims.add-person'])">
                                                    <v-col cols="1">
                                                        <v-spacer class="ma-2"></v-spacer>
                                                    </v-col>
                                                    <v-col cols="4" class="text-right">
                                                        <v-btn class="ma-0 pa-2" density="compact" color="primary" @click="AssignAdjusterToClaim(item.raw.claimid)">Assign</v-btn>
                                                    </v-col>
                                                    <v-col cols="2">
                                                        <v-spacer class="ma-2"></v-spacer>
                                                    </v-col>
                                                    <v-col cols="4" class="text-left">
                                                        <v-btn class="ma-0 pa-2" density="compact" color="warning" @click="RevokeAdjusterAssignment(item.raw.claimid)">Reject</v-btn>
                                                    </v-col>
                                                    <v-col cols="1">
                                                        <v-spacer class="ma-2"></v-spacer>
                                                    </v-col>
                                                </v-row>
                                            </v-card-text>
                                        </v-card>
                                    </template>
                                </template>
                            </v-data-iterator>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

        </v-col>
    </v-row>

    <v-row>
        <v-col cols="12">
            <v-spacer class="ma-5"></v-spacer>
        </v-col>
    </v-row>
</template>
